import { Button, Table, Select, Typography, Row, Col, Form, Card, Modal, Popconfirm, Space } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  AwardEventsDetails,
  AwardNominationsDetails,
  addAwardNomination,
  AwardNominationsUpdateAction,
} from "../../redux/rtk/features/award/awardSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import "./Award.css";

const { Option } = Select;
const { Title } = Typography;

const AwardNominations = () => {
  const AwardEvents = useSelector((state) => state?.award?.AwardEvents);
  const AwardNominations = useSelector((state) => state?.award?.AwardNominations);
  const users = useSelector((state) => state?.users?.list);
  const dispatch = useDispatch();
  const [formLoading, setFormLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); 
  const [nominationModalOpen, setNominationModalOpen] = useState(false);
  const [form] = Form.useForm();
  const EmployeeId = getUserFromToken();


  useEffect(() => {
    dispatch(AwardEventsDetails());
    dispatch(AwardNominationsDetails());
    dispatch(loadAllStaff({ status: true }));
  }, [dispatch]);

  // Handle form submission
  const onFinish = async (values) => {
    setFormLoading(true);
    try {
      const nominationData = {
        AwardNominationId: 0,
        AwardEventId: parseInt(values.AwardEventId),
        NomineeId: parseInt(values.NomineeId),
        DataStatus: 1,
      };
      const resp = await dispatch(addAwardNomination(nominationData));

      if (resp.payload.message === "success") {
        toast.success("Nomination added successfully");
        form.resetFields();
        dispatch(AwardNominationsDetails());
        setNominationModalOpen(false);
      } else {
        toast.error("Failed to add nomination");
      }
    } catch (error) {
      toast.error("An error occurred while adding the nomination");
      // console.error("Form submission error:", error);
    } finally {
      setFormLoading(false);
    }
  };

  const onFinishFailed = () => {
    toast.warning("Please fill all required fields!");
  };

  // Handle approve/reject action
  const handleAction = async (nominationId, statusId) => {
    setActionLoading((prev) => ({ ...prev, [nominationId]: true }));
    try {
      const actionData = {
        AwardNominationId: parseInt(nominationId),
        AwardStatusId: statusId,
        ActionTakenById: parseInt(EmployeeId),
        DataStatus:1
      };
      const resp = await dispatch(AwardNominationsUpdateAction(actionData));

      if (resp.payload.message === "success") {
        toast.success(`Nomination ${statusId === 1 ? "approved" : "rejected"} successfully`);
        dispatch(AwardNominationsDetails()); // Refresh nominations
      } else {
        toast.error(`Failed to ${statusId === 1 ? "approve" : "reject"} nomination`);
      }
    } catch (error) {
      toast.error(`Error ${statusId === 1 ? "approving" : "rejecting"} nomination`);
      // console.error("Action error:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [nominationId]: false }));
    }
  };

  const columns = [
    {
      title: "Award Event Name",
      dataIndex: "AwardEventName",
      key: "AwardEventName",
    },
    {
      title: "Description",
      dataIndex: "AwardEventDescription",
      key: "AwardEventDescription",
    },
    {
      title: "Event Date",
      dataIndex: "AwardEventDate",
      key: "AwardEventDate",
      render: (text) => (text ? dayjs(text).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Nominee",
      dataIndex: "NomineeName",
      key: "NomineeName",
    },
    {
      title: "Status",
      dataIndex: "AwardStatusName",
      key: "AwardStatusName",
    },
    {
      title: "Action Taken By",
      dataIndex: "ActionTakenByName",
      key: "ActionTakenByName",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to approve this nomination?"
            onConfirm={() => handleAction(record.AwardNominationId, 1)}
            okText="Yes"
            cancelText="No"
            disabled={record.AwardStatusId === 1 || record.AwardStatusId === 2}
          >
            <Button
            //   type="primary"
              style={{ backgroundColor: "green", borderColor: "green" }}
              size="small"
              loading={actionLoading[record.AwardNominationId]}
              disabled={record.AwardStatusId === 1 || record.AwardStatusId === 2}
            >
              Approve
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Are you sure you want to reject this nomination?"
            onConfirm={() => handleAction(record.AwardNominationId, 0)}
            okText="Yes"
            cancelText="No"
            disabled={record.AwardStatusId === 1 || record.AwardStatusId === 0}
          >
            <Button
              type="danger"
              size="small"
              loading={actionLoading[record.AwardNominationId]}
              disabled={record.AwardStatusId === 1 || record.AwardStatusId === 0}
            >
              Reject
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="award-nominations-page-container">
      <div className="flex justify-between items-center mb-4">
        <span />
        <Button
          type="primary"
          className="award-apply-btn"
          onClick={() => setNominationModalOpen(true)}
        >
          Add Nomination
        </Button>
      </div>
      <Row gutter={[16, 16]} className="award-nominations-row">
        <Col xs={24} className="award-nominations-table-col">
          <div className="award-nominations-table-wrapper">
            <Table
              columns={columns}
              dataSource={AwardNominations || []}
              rowKey="AwardNominationId"
              pagination={false}
              loading={!AwardNominations}
              bordered
              scroll={{ x: true, y: "100%" }}
            />
          </div>
        </Col>
      </Row>
      <Modal
        title="Add Nomination"
        open={nominationModalOpen}
        onCancel={() => setNominationModalOpen(false)}
        footer={null}
        width={440}
        destroyOnClose
      >
        <Form
          form={form}
          name="add-nomination"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            label="Award Event"
            name="AwardEventId"
            rules={[
              {
                required: true,
                message: "Please select an award event!",
              },
            ]}
          >
            <Select
              placeholder="Select Award Event"
              loading={!AwardEvents}
            >
              {AwardEvents &&
                AwardEvents.map((event) => (
                  <Option key={event.AwardEventId} value={event.AwardEventId}>
                    {event.AwardEventName}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Nominee"
            name="NomineeId"
            rules={[
              {
                required: true,
                message: "Please select a nominee!",
              },
            ]}
          >
            <Select
              placeholder="Select Nominee"
              loading={!users}
            >
              {users &&
                users.map((user) => (
                  <Option key={user.EmployeeId} value={user.EmployeeId}>
                    {user.EmployeeName}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={formLoading}
            >
              Add Nomination
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AwardNominations;